import { useState, useEffect } from "react";
import { Row, Col, Typography, Card, Form, Input, Button, Table, Select, Space, message, Modal, Popconfirm, Tag, InputNumber } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import LayoutPage from "../../layouts/Main";
import AccountService from "../../utils/api/financial/accounts";
import '../../scss/accountsStyle.scss';

const { Title, Text } = Typography;
const { Option } = Select;

function AccountsList() {
  const [form] = Form.useForm();
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);


  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [filterYear, setFilterYear] = useState(currentYear);
  const [filterMonth, setFilterMonth] = useState(currentMonth);
  const [filterBank, setFilterBank] = useState(null);

  const [banks, setBanks] = useState([
    { id: 1, name: "BBVA" },
    { id: 2, name: "BANBAJIO" },

  ]);
  const [currencies, setCurrencies] = useState([
    { id: 1, name: "MXN", symbol: "$" },
    { id: 2, name: "USD", symbol: "US$" },

  ]);

  const months = [
    { value: 1, label: "Enero" },
    { value: 2, label: "Febrero" },
    { value: 3, label: "Marzo" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Mayo" },
    { value: 6, label: "Junio" },
    { value: 7, label: "Julio" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Septiembre" },
    { value: 10, label: "Octubre" },
    { value: 11, label: "Noviembre" },
    { value: 12, label: "Diciembre" },
  ];

  // Generar años (del 2020 al 2030)
  const years = Array.from({ length: 11 }, (_, i) => ({
    value: 2020 + i,
    label: 2020 + i
  }));

  useEffect(() => {
    loadAccounts();
  }, []);

  // Aplicar filtros cuando cambian
  useEffect(() => {
    applyFilters();
  }, [filterYear, filterMonth, filterBank, allAccounts]);

  const applyFilters = () => {
    let filtered = allAccounts;

    if (filterYear) {
      filtered = filtered.filter(acc => acc.year === filterYear);
    }
    if (filterMonth) {
      filtered = filtered.filter(acc => acc.month === filterMonth);
    }
    if (filterBank) {
      filtered = filtered.filter(acc => acc.bank === filterBank);
    }

    setAccounts(filtered);
  };

  const handleClearFilters = () => {
    setFilterYear(null);
    setFilterMonth(null);
    setFilterBank(null);
  };

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const response = await AccountService.ListAccounts();

      
      let accountsData = [];

      if (response && response.data) {
        if (Array.isArray(response.data)) {
          accountsData = response.data;
        } else if (Array.isArray(response)) {
          accountsData = response;
        } else if (response.data && Array.isArray(response.data.data)) {
          accountsData = response.data.data;
        }
      }

      setAllAccounts(accountsData || []);
      setAccounts(accountsData || []);
    } catch (error) {
      console.error("Error loading accounts:", error);
      message.error("Error al cargar las cuentas");

    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async (values) => {
    try {

      const messageKey = "saving";
      message.loading({ content: "Guardando...", key: messageKey });
      const account = {
        year: values.year,
        month: values.month,
        bank: values.bank,
        currency_type: values.currency_type,
        amount: values.amount,       
        final_balance: values.final_balance,               
        created_by: "Admin"        
      };
      console.log("Account to save:", account);
      if (editingId) {
        await AccountService.EditAccount(editingId, account);
        message.success({ content: "Cuenta actualizada", key: messageKey });
      } else {
        await AccountService.AddAccount(account);
        message.success({ content: "Cuenta agregada", key: messageKey });
      }

      form.resetFields();
      setModalVisible(false);
      setEditingId(null);
      loadAccounts();
    } catch (error) {
      console.error("Error saving account:", error);
      const apiMessage =
        error?.data?.error ||
        error?.response?.data?.error ||
        error?.data?.message ||
        error?.message ||
        "Error al guardar la cuenta";

      message.error(apiMessage);
    }
  };

  const handleOpenModal = () => {
    setEditingId(null);

    const currentYear = new Date().getFullYear();

    form.setFieldsValue({
      year: currentYear,
    });
    form.setFieldsValue({
      month: currentMonth,
    });

    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await AccountService.DeleteAccount(id);
      message.success("Cuenta eliminada");
      loadAccounts();
    } catch (error) {
      console.error("Error deleting account:", error);
      message.error("Error al eliminar la cuenta");
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingId(null);
    form.resetFields();
  };

  const columns = [
    {
      title: "Año",
      dataIndex: "year",
      key: "year",
      align: "center",
      width: "20%",
      sorter: (a, b) => a.year - b.year,
    },
    {
      title: "Mes",
      dataIndex: "month",
      key: "month",
      align: "center",
      width: "20%",
      render: (month) => {
        return months.find(m => m.value === month)?.label;
      },
      sorter: (a, b) => a.month - b.month,
    },
    {
      title: "Banco",
      dataIndex: "bank",
      key: "bank",
      align: "center",
      width: "20%",
    },
    {
      title: "Moneda",
      dataIndex: "currency_type",
      key: "currency_type",
      align: "center",
      width: "10%",
      render: (currency) => (
        <Tag>{currency}</Tag>
      ),
    },
    {
      title: "Monto",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: "10%",
      render: (amount, record) => {
        const currencySymbol =
          currencies.find(c => c.name === record.currency)?.symbol || "$";

        return `${currencySymbol} ${amount?.toLocaleString() || 0}`;
      },
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "Saldo Final",
      dataIndex: "final_balance",
      key: "final_balance",
      align: "right",
      width: "15%",
      render: (finalBalance, record) => {
        const currencySymbol =
          currencies.find(c => c.name === record.currency)?.symbol || "$";

        return `${currencySymbol} ${finalBalance?.toLocaleString() || 0}`;
      },
      sorter: (a, b) => a.finalBalance - b.finalBalance,
    },
    {
      title: "Acciones",
      key: "actions",
      width: "20%",
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="50px"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />

          <Popconfirm
            title="Eliminar cuenta"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              danger
              size="50px"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <LayoutPage>
      <div className="accounts-container">
        <Row gutter={[24, 24]} style={{ marginBottom: 8 }}>
          <Col span={24}>            
            <Title level={1} style={{ color: "#1E3A8A", fontWeight: 600 }}>
            CUENTAS DE BANCO</Title>
          </Col>
        </Row>
          <Row gutter={[24, 24]} style={{ marginBottom: 14 }}>
          <Col span={24}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenModal}
                size="large"
                style={{
                  background: "#0F5197",
                  border: "none",
                  borderRadius: 12,
                  height: 48,
                  paddingInline: 28,
                  fontWeight: 600,
                  boxShadow: "0 6px 18px rgba(37,99,235,.18)",
                }}
              >
                Agregar Datos
              </Button>
            </div>
          </Col>
        </Row>

        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={24} md={24} lg={24}>
            <Card
              style={{
                borderRadius: 10,
                border: "1px solid #F1F5F9",
                boxShadow: "0 4px 20px rgba(15,23,42,.05)",
                padding: "30px",
              }}
            >
              <Row gutter={[24, 24]}>

                <Col span={24}>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "16px",
                      alignItems: "flex-end",
                    }}
                  >
                    {/* Año */}
                    <div>
                      <label
                        style={{
                          color: "#1E3A8A",
                          fontWeight: 600,
                          display: "block",
                          marginBottom: 8,
                        }}
                      >
                        Año
                      </label>

                      <Select
                        placeholder="Seleccionar año"
                        value={filterYear}
                        onChange={setFilterYear}
                        allowClear
                        style={{ width: 180 }}
                        size="large"
                      >
                        {years.map((year) => (
                          <Option key={year.value} value={year.value}>
                            {year.label}
                          </Option>
                        ))}
                      </Select>
                    </div>

                    {/* Mes */}
                    <div>
                      <label
                        style={{
                          color: "#1E3A8A",
                          fontWeight: 600,
                          display: "block",
                          marginBottom: 8,
                        }}
                      >
                        Mes
                      </label>

                      <Select
                        placeholder="Seleccionar mes"
                        value={filterMonth}
                        onChange={setFilterMonth}
                        allowClear
                        style={{ width: 180 }}
                        size="large"
                      >
                        {months.map((month) => (
                          <Option key={month.value} value={month.value}>
                            {month.label}
                          </Option>
                        ))}
                      </Select>
                    </div>


                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      {banks.map((bank) => (
                        <Button
                          key={bank.id}
                          onClick={() =>
                            setFilterBank(
                              filterBank === bank.name ? null : bank.name
                            )
                          }
                          size="large"
                          style={{
                            height: 46,
                            paddingInline: 24,
                            borderRadius: 12,
                            border:
                              filterBank === bank.name
                                ? "1px solid #2563EB"
                                : "1px solid #d9d9d9",

                            background:
                              filterBank === bank.name
                                ? "#EFF6FF"
                                : "#fff",

                            color:
                              filterBank === bank.name
                                ? "#2563EB"
                                : "#374151",

                            fontWeight: 600,
                          }}
                        >
                          {bank.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                </Col>



              </Row>
            </Card>
          </Col>
        </Row>
      

        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Card
              style={{ borderRadius: "8px" }}
              loading={loading}
            >
              <Table
                columns={columns}
                dataSource={Array.isArray(accounts) ? accounts : []}
                rowKey="id"
                bordered
                size="middle"

                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  responsive: true,
                  showTotal: (total) => `Total: ${total} cuentas`,
                }}
                style={{ width: "100%" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Modal para agregar/editar */}
        <Modal
          title={
            <Text style={{ color: "#1E3A8A", fontWeight: 600, fontSize: 16 }}>
              {editingId ? "Editar Cuenta" : "Nueva Cuenta"}
            </Text>
          }
          visible={modalVisible}
          onCancel={handleCloseModal}
          footer={null}
          width={800}
          style={{ borderRadius: "8px" }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddAccount}
            style={{ marginTop: 24 }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Año"
                  name="year"
                  rules={[{ required: true, message: "Selecciona el año" }]}
                >
                  <Select placeholder="Seleccionar año">
                    {years.map(year => (
                      <Option key={year.value} value={year.value}>
                        {year.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Mes"
                  name="month"
                  rules={[{ required: true, message: "Selecciona el mes" }]}
                >
                  <Select placeholder="Seleccionar mes">
                    {months.map(month => (
                      <Option key={month.value} value={month.value}>
                        {month.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>


              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Banco"
                  name="bank"
                  rules={[{ required: true, message: "Selecciona un banco" }]}
                >
                  <Select placeholder="Seleccionar banco">
                    {banks.map(bank => (
                      <Option key={bank.id} value={bank.name}>
                        {bank.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Moneda"
                  name="currency_type"
                  rules={[{ required: true, message: "Selecciona una moneda" }]}
                >
                  <Select placeholder="Seleccionar moneda">
                    {currencies.map(currency => (
                      <Option key={currency.id} value={currency.name}>
                        {currency.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Monto"
                  name="amount"
                  rules={[{ required: true, message: "Ingresa el monto" }]}
                >
                  <InputNumber
                    placeholder="0.00"
                    style={{ width: "100%" }}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Saldo Final"
                  name="final_balance"
                  rules={[{ required: true, message: "Ingresa el saldo final" }]}
                >
                  <InputNumber
                    placeholder="0.00"
                    style={{ width: "100%" }}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 24 }}>
              <Col xs={24} style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <Button onClick={handleCloseModal}>
                  Cancelar
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ backgroundColor: "#1E3A8A" }}
                >
                  {editingId ? "Actualizar" : "Guardar"}
                </Button>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </LayoutPage>
  );
}

export default AccountsList;
